import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Grid,
    TextField
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { t } from 'i18next';

import { useUser } from '../../../hooks/useUser';
import { addAppointmentNotes } from '../../../services/appointments';

const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);

    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();

    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
    }

    return age;
};

const calculateBMI = (weight, height) => {
    if (!weight || !height) return '';
    const h = height / 100;
    return (weight / (h * h)).toFixed(1);
};

const CreateConsultationPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { user } = useUser();

    const patient = state?.patient;
    const appointmentUuid = state?.appointmentUuid;
    const from = state?.from;
    const seeUserState = state?.seeUserState;

    const [identification, setIdentification] = useState({
        name: patient?.name || '',
        sex: patient?.sex || '',
        birthdate: patient?.birth_date || '',
        age: calculateAge(patient?.birth_date),
        weight: patient?.weight || '',
        height: patient?.height || '',
        bmi: calculateBMI(patient?.weight, patient?.height),
        processNumber: patient?.health_id || ''
    });

    const [generalData, setGeneralData] = useState({
        origin: user?.selectedClinic?.name || '',
        doctor: user?.name || '',
        operators: ''
    });

    const [clinicalInfo, setClinicalInfo] = useState('');

    const [consultationNotes, setConsultationNotes] = useState('');

    useEffect(() => {
        setIdentification((prev) => ({
            ...prev,
            bmi: calculateBMI(prev.weight, prev.height)
        }));
    }, [identification.weight, identification.height]);

    if (!patient || !appointmentUuid) {
        return <Typography>Dados inválidos</Typography>;
    }

    const handleCreate = async () => {
        const buildNotes = () => `
        IDENTIFICAÇÃO
        Nome: ${identification.name}
        Sexo: ${identification.sex}
        Data de nascimento: ${identification.birthdate}
        Idade: ${identification.age}
        Peso: ${identification.weight}
        Altura: ${identification.height}
        IMC: ${identification.bmi}
        Processo: ${identification.processNumber}
        
        DADOS GERAIS
        Origem: ${generalData.origin}
        Médico: ${generalData.doctor}
        Operadores: ${generalData.operators}
        
        INFORMAÇÃO CLÍNICA
        ${clinicalInfo}
        
        NOTAS DA CONSULTA
        ${consultationNotes}
        `;

        try {
            await addAppointmentNotes({
                uuid: appointmentUuid,
                notes: buildNotes()
            });

            navigate(from.pathname, {
                state: {
                    ...from.state,
                    user: patient,
                    restoredState: seeUserState
                }
            });
        } catch (error) {
            console.error('Erro ao registar notas da consulta', error);
        }
    };

    const handleBack = () => {
        if (from) {
            navigate(from.pathname, {
                state: {
                    ...from.state,
                    user: patient,
                    restoredState: seeUserState
                }
            });
        } else {
            navigate(-1);
        }
    };

    return (
        <Box mt={2}>
            <Card>
                <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                    <Typography variant="h5" fontWeight={700} mb={1}>
                        Nova Consulta
                    </Typography>

                    <Box
                        sx={{
                            height: 3,
                            width: 80,
                            backgroundColor: '#1976d2',
                            borderRadius: 2,
                            mb: 3
                        }}
                    />

                    <Grid container spacing={2}>
                        {/* IDENTIFICAÇÃO */}
                        <Grid item xs={12}>
                            <Typography variant="h5">
                                {t('stress.identification')}
                            </Typography>
                        </Grid>

                        {Object.entries(identification).map(([key, value]) => (
                            <Grid item xs={12} md={4} key={key}>
                                <TextField
                                    fullWidth
                                    label={key}
                                    value={value}
                                    disabled={
                                        key === 'name' ||
                                        key === 'sex' ||
                                        key === 'birthdate' ||
                                        key === 'age' ||
                                        key === 'bmi'
                                    }
                                    onChange={(e) =>
                                        setIdentification((prev) => ({
                                            ...prev,
                                            [key]: e.target.value
                                        }))
                                    }
                                />
                            </Grid>
                        ))}

                        {/* DADOS GERAIS */}
                        <Grid item xs={12}>
                            <Typography variant="h5">{t('stress.data')}</Typography>
                        </Grid>

                        {Object.entries(generalData).map(([key, value]) => (
                            <Grid item xs={12} md={4} key={key}>
                                <TextField
                                    fullWidth
                                    label={key}
                                    value={value}
                                    onChange={(e) =>
                                        setGeneralData((prev) => ({
                                            ...prev,
                                            [key]: e.target.value
                                        }))
                                    }
                                />
                            </Grid>
                        ))}

                        {/* INFORMAÇÃO CLÍNICA */}
                        <Grid item xs={12}>
                            <Typography variant="h5">{t('stress.info')}</Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label={t('stress.indication')}
                                value={clinicalInfo}
                                onChange={(e) => setClinicalInfo(e.target.value)}
                            />
                        </Grid>

                        {/* CONTEÚDO DA CONSULTA */}
                        <Grid item xs={12}>
                            <Typography variant="h5">Notas da Consulta</Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={6}
                                label="Descrição / relatório da consulta"
                                value={consultationNotes}
                                onChange={(e) => setConsultationNotes(e.target.value)}
                            />
                        </Grid>
                    </Grid>

                    <Box display="flex" justifyContent="flex-end" mt={4} gap={2}>
                        <Button variant="outlined" onClick={handleBack}>
                            {t('stress.cancel')}
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleCreate}
                            disabled={!consultationNotes}
                        >
                            Registar Consulta
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CreateConsultationPage;
